// useVideoData.js - Version corrigée avec ordre des fonctions approprié

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import videoFirebaseService from './VideoFirebaseService';

export const useVideoData = (userId, selectedTheme, searchQuery, selectedObjectiveForVideos, VIDEO_THEMES, filters) => {
  // ================================
  // ÉTATS PRINCIPAUX
  // ================================
  
  const [videosByTheme, setVideosByTheme] = useState({});
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryMessage, setDiscoveryMessage] = useState('');
  
  // États pour l'analyse sémantique
  const [analysisInfo, setAnalysisInfo] = useState({
    totalAnalyzed: 0,
    relevantFound: 0,
    lastAnalysis: null,
    mode: 'theme'
  });

  // Refs pour éviter les re-renders - CRITICAL
  const loadingRef = useRef(false);
  const lastParamsRef = useRef({
    userId: null,
    selectedTheme: null,
    searchQuery: null,
    objectiveId: null
  });

  // ================================
  // DÉTECTION DU MODE (STABLE)
  // ================================

  const currentMode = useMemo(() => {
    if (selectedObjectiveForVideos?.id) return 'objective';
    if (searchQuery?.trim()) return 'search';
    return 'theme';
  }, [selectedObjectiveForVideos?.id, searchQuery]);

  // ================================
  // FONCTIONS DE DÉCOUVERTE (DÉCLARÉES EN PREMIER)
  // ================================

  const handleDiscoverForObjective = useCallback(async (objective) => {
    if (!objective?.id || !userId || isDiscovering) return { success: false };
    
    try {
      setIsDiscovering(true);
      setDiscoveryMessage(`🔍 Découverte pour "${objective.title}"...`);
      
      const result = await videoFirebaseService.discoverAndAnalyzeForObjective(
        objective,
        userId,
        { maxVideos: 20 }
      );
      
      if (result.success && result.added > 0) {
        setDiscoveryMessage(`✅ ${result.added} vidéos découvertes`);
        
        // Recharger après un délai
        setTimeout(() => {
          loadVideos();
        }, 1500);
        
        return {
          success: true,
          added: result.added,
          analyzed: result.analyzed,
          dataReady: true
        };
      } else {
        setDiscoveryMessage('⚠️ Aucune nouvelle vidéo trouvée');
        return { success: false, dataReady: false };
      }
      
    } catch (error) {
      console.error('❌ Erreur découverte objectif:', error);
      setDiscoveryMessage('❌ Erreur lors de la découverte');
      return { success: false, error: error.message };
    } finally {
      setTimeout(() => {
        setIsDiscovering(false);
        setDiscoveryMessage('');
      }, 3000);
    }
  }, [userId, isDiscovering]); // Suppression de loadVideos des dépendances

  const handleDiscoverTheme = useCallback(async (theme) => {
    if (!userId || !theme || isDiscovering) return;
    
    try {
      setIsDiscovering(true);
      setDiscoveryMessage(`🔍 Découverte ${theme}...`);
      
      const result = await videoFirebaseService.discoverVideosByTheme(theme, userId, {
        maxVideos: 15
      });
      
      if (result.added > 0) {
        setDiscoveryMessage(`✅ ${result.added} nouvelles vidéos pour ${theme}`);
        
        // Mettre à jour les données pour ce thème
        const updatedVideos = await videoFirebaseService.getVideosByTheme(theme, 50, userId);
        setVideosByTheme(prev => ({ ...prev, [theme]: updatedVideos }));
        
        if (selectedTheme === theme) {
          setFilteredVideos(updatedVideos);
        }
      } else {
        setDiscoveryMessage(`ℹ️ Aucune nouvelle vidéo pour ${theme}`);
      }
      
    } catch (error) {
      console.error('❌ Erreur découverte thème:', error);
      setDiscoveryMessage('❌ Erreur lors de la découverte');
    } finally {
      setTimeout(() => {
        setIsDiscovering(false);
        setDiscoveryMessage('');
      }, 3000);
    }
  }, [userId, selectedTheme, isDiscovering]);

  const handleCustomDiscovery = useCallback(async (searchQuery) => {
    if (!userId || !searchQuery?.trim() || isDiscovering) return;
    
    try {
      setIsDiscovering(true);
      setDiscoveryMessage(`🔍 Recherche avancée: "${searchQuery}"...`);
      
      const result = await videoFirebaseService.searchVideosByQuery(
        searchQuery,
        userId,
        { maxResults: 25, useSemanticAnalysis: true }
      );
      
      if (result.results && result.results.length > 0) {
        setFilteredVideos(result.results);
        setDiscoveryMessage(`✅ ${result.results.length} vidéos trouvées`);
        
        setAnalysisInfo({
          totalAnalyzed: result.results.length,
          relevantFound: result.results.length,
          lastAnalysis: new Date().toISOString(),
          mode: 'semantic_search'
        });
      } else {
        setDiscoveryMessage('ℹ️ Aucune vidéo pertinente trouvée');
      }
      
    } catch (error) {
      console.error('❌ Erreur recherche personnalisée:', error);
      setDiscoveryMessage('❌ Erreur lors de la recherche');
    } finally {
      setTimeout(() => {
        setIsDiscovering(false);
        setDiscoveryMessage('');
      }, 3000);
    }
  }, [userId, isDiscovering]);

  // ================================
  // FONCTIONS DE CHARGEMENT
  // ================================

  const loadVideosByObjective = useCallback(async () => {
    if (!selectedObjectiveForVideos?.id || !userId) return [];
    
    try {
      console.log(`🎯 Chargement vidéos pour objectif: ${selectedObjectiveForVideos.title}`);
      
      const objectiveVideos = await videoFirebaseService.getVideosByObjective(
        selectedObjectiveForVideos.id, 
        userId,
        { limit: 50 }
      );
      
      // NOUVEAU: Gestion du cas vide avec auto-découverte
      if (objectiveVideos.isEmpty && objectiveVideos.needsDiscovery) {
        console.log('🔄 Aucune vidéo trouvée - Lancement auto-découverte...');
        
        // Déclencher automatiquement la découverte après un petit délai
        setTimeout(async () => {
          try {
            const discoveryResult = await handleDiscoverForObjective(selectedObjectiveForVideos);
            
            if (!discoveryResult.success) {
              // Si la découverte échoue, on garde quand même l'état vide
              setAnalysisInfo({
                totalAnalyzed: 0,
                relevantFound: 0,
                lastAnalysis: new Date().toISOString(),
                mode: 'objective'
              });
            }
            
          } catch (error) {
            console.error('❌ Erreur auto-découverte:', error);
          }
        }, 500);
        
        // Retourner un tableau vide en attendant la découverte
        setAnalysisInfo({
          totalAnalyzed: 0,
          relevantFound: 0,
          lastAnalysis: null,
          mode: 'objective'
        });
        return [];
      }
      
      // Cas normal : vidéos trouvées
      setAnalysisInfo({
        totalAnalyzed: objectiveVideos.length,
        relevantFound: objectiveVideos.length,
        lastAnalysis: new Date().toISOString(),
        mode: 'objective'
      });
      
      console.log(`✅ ${objectiveVideos.length} vidéos pré-analysées trouvées`);
      return Array.isArray(objectiveVideos) ? objectiveVideos : [];
      
    } catch (error) {
      console.error('❌ Erreur chargement objectif:', error);
      setError('Erreur chargement vidéos pour objectif');
      return [];
    }
  }, [selectedObjectiveForVideos?.id, selectedObjectiveForVideos?.title, userId, handleDiscoverForObjective]);

  const loadVideosByTheme = useCallback(async () => {
    if (!selectedTheme || !userId) return [];
    
    try {
      console.log(`🎨 Chargement vidéos par thème: ${selectedTheme}`);
      
      const themeVideos = await videoFirebaseService.getVideosByTheme(selectedTheme, 50, userId);
      
      // Mettre à jour videosByTheme
      setVideosByTheme(prev => ({
        ...prev,
        [selectedTheme]: themeVideos
      }));
      
      setAnalysisInfo(prev => ({
        ...prev,
        totalAnalyzed: 0,
        relevantFound: themeVideos.length,
        lastAnalysis: null,
        mode: 'theme'
      }));
      
      return themeVideos;
      
    } catch (error) {
      console.error('❌ Erreur chargement thème:', error);
      setError(`Erreur chargement thème ${selectedTheme}`);
      return [];
    }
  }, [selectedTheme, userId]);

  const loadVideosBySearch = useCallback(async () => {
    if (!searchQuery?.trim() || !userId) return [];
    
    try {
      console.log(`🔍 Recherche vidéos: "${searchQuery}"`);
      
      const searchResult = await videoFirebaseService.searchVideosByQuery(
        searchQuery,
        userId,
        { maxResults: 30, useSemanticAnalysis: false }
      );
      
      setAnalysisInfo({
        totalAnalyzed: 0,
        relevantFound: searchResult.results?.length || 0,
        lastAnalysis: new Date().toISOString(),
        mode: 'search',
        hasSemanticOption: searchResult.hasSemanticOption
      });
      
      return searchResult.results || [];
      
    } catch (error) {
      console.error('❌ Erreur recherche:', error);
      setError('Erreur lors de la recherche');
      return [];
    }
  }, [searchQuery, userId]);

  // ================================
  // FONCTION DE CHARGEMENT PRINCIPALE
  // ================================

  const loadVideos = useCallback(async () => {
    // Éviter les appels multiples simultanés
    if (loadingRef.current) {
      console.log('⏳ Chargement déjà en cours, abandon');
      return;
    }

    // Vérifier si les paramètres ont vraiment changé
    const currentParams = {
      userId,
      selectedTheme,
      searchQuery: searchQuery || '',
      objectiveId: selectedObjectiveForVideos?.id || null
    };

    const paramsChanged = JSON.stringify(currentParams) !== JSON.stringify(lastParamsRef.current);
    
    if (!paramsChanged) {
      console.log('📋 Paramètres inchangés, pas de rechargement');
      return;
    }

    if (!userId) {
      console.log('⚠️ Pas d\'userId, abandon du chargement');
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      let videos = [];
      
      switch (currentMode) {
        case 'objective':
          videos = await loadVideosByObjective();
          break;
        case 'search':
          videos = await loadVideosBySearch();
          break;
        case 'theme':
        default:
          videos = await loadVideosByTheme();
          break;
      }
      
      setFilteredVideos(videos);
      lastParamsRef.current = currentParams;
      
      console.log(`✅ ${videos.length} vidéos chargées en mode ${currentMode}`);
      
    } catch (error) {
      console.error('❌ Erreur chargement principal:', error);
      setError('Erreur lors du chargement des vidéos');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [currentMode, loadVideosByObjective, loadVideosBySearch, loadVideosByTheme, userId, selectedTheme, searchQuery, selectedObjectiveForVideos?.id]);

  // ================================
  // AUTRES FONCTIONS
  // ================================

  const handleVideoView = useCallback(async (videoId) => {
    try {
      await videoFirebaseService.recordVideoView(videoId, userId, selectedTheme);
    } catch (error) {
      console.warn('Erreur vue vidéo:', error);
    }
  }, [userId, selectedTheme]);

  // ================================
  // EFFET PRINCIPAL (OPTIMISÉ)
  // ================================

  useEffect(() => {
    if (userId) {
      const timer = setTimeout(() => {
        loadVideos();
      }, 100); // Petit délai pour éviter les appels trop fréquents
      
      return () => clearTimeout(timer);
    }
  }, [userId, loadVideos]);

  // ================================
  // MISE À JOUR DU MODE
  // ================================

  useEffect(() => {
    setAnalysisInfo(prev => ({
      ...prev,
      mode: currentMode
    }));
  }, [currentMode]);

  // ================================
  // APPLICATION DES FILTRES
  // ================================

  useEffect(() => {
    if (filters && Object.keys(filters).some(key => filters[key] !== 'all')) {
      applyFilters();
    }
  }, [filters]);

  const applyFilters = useCallback(() => {
    if (!filters) return;
    
    setFilteredVideos(currentVideos => {
      let filtered = [...currentVideos];
      
      if (filters.discoveryMethod !== 'all') {
        filtered = filtered.filter(v => {
          if (filters.discoveryMethod === 'sourceservice') {
            return v.discoveryMethod === 'sourceservice' || v.source === 'sourceservice_discovery';
          }
          return v.discoveryMethod === filters.discoveryMethod;
        });
      }
      
      if (filters.qualityScore !== 'all') {
        const minScore = parseInt(filters.qualityScore);
        filtered = filtered.filter(v => (v.qualityRating || v.popularityScore || v.semanticScore || 5) >= minScore);
      }

      if (filters.duration !== 'all') {
        filtered = filtered.filter(v => {
          const duration = v.duration || '10:00';
          const minutes = parseDuration(duration);
          
          switch (filters.duration) {
            case 'short': return minutes < 10;
            case 'medium': return minutes >= 10 && minutes <= 30;
            case 'long': return minutes > 30;
            default: return true;
          }
        });
      }
      
      return filtered;
    });
  }, [filters]);

  const parseDuration = useCallback((duration) => {
    if (!duration) return 10;
    const parts = duration.split(':');
    if (parts.length === 2) {
      return parseInt(parts[0]) + (parseInt(parts[1]) / 60);
    }
    return 10;
  }, []);

  // ================================
  // RETOUR DU HOOK
  // ================================

  return {
    // Données principales
    videosByTheme,
    filteredVideos,
    
    // États de chargement
    loading,
    error,
    isDiscovering,
    discoveryMessage,
    
    // Informations d'analyse
    semanticAnalysisInfo: analysisInfo,
    analysisProgress: { 
      isAnalyzing: false, 
      progress: 0, 
      current: 0, 
      total: 0 
    },
    
    // Mode actuel
    currentSearchMode: analysisInfo.mode,
    
    // Fonctions principales
    loadVideos,
    handleDiscoverTheme,
    handleDiscoverForObjective,
    handleCustomDiscovery,
    handleVideoView,
    
    // Compatibilité
    customSearchResults: analysisInfo.mode === 'search' ? filteredVideos : []
  };
};